// src/pages/reports/ReportMaster.tsx
import React, { useState, useRef, useEffect } from "react";
import { Search } from "@mui/icons-material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CalendorIcon from "../../../../src/assests/images/calendar-2.svg";
import {
  Box,
  Link,
  Typography,
  TextField,
  InputAdornment,
  Button,
  ButtonGroup,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { debounce } from "lodash";
import ReportsMasterTable from "./ReportsMasterTable";
import ReportsMasterTrTable from "./ReportMasterTrTable";
import ReportsFilterPopup from "./ReportsFilterPopup";
import ReportsTrFilterPopup from "./ReportsTrFilterPopup";
import arrowLeft from "../../../../src/assests/images/arrow-left.svg";
import arrowRight from "../../../../src/assests/images/arrow-right.png";
import doubleArrowLeft from "../../../../src/assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../../src/assests/images/double-arrow-right.svg";

// Combined payload so one type works for both popups
type ReportsFilterPayload = {
  // Summary (legacy)
  locations?: string[] | null;
  outletTypes?: string[] | null;
  outletNames?: string[] | null;
  pmiEntityCodes?: string[] | null;
  lastloginStart?: string | null;
  lastloginEnd?: string | null;
  memberSinceStart?: string | null;
  memberSinceEnd?: string | null;

  // Transactions (v2)
  region?: string[] | null;
  regions? : string [] |null;
  asm_name?: string[] | null;
  area_code?: string[] | null;
  tse_name?: string[] | null;
  territory_code?: string[] | null;
  sfa_id?: string[] | null;
  outlet_salesforce_id?: string[] | null;
  pmi_entity_code?: string[] | null;
  status?: string[] | null;
  contract_since_from?: string | null;
  contract_since_to?: string | null;

  // shared
  backendFilters?: Record<string, any> | null;

  // allow extra keys safely
  [k: string]: any;
};

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, onClear }, ref) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ccc",
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "0 10px",
        height: "48px",
        width: "301px",
        cursor: "pointer",
      }}
    >
      <input
        ref={ref}
        value={value || "Select Date Range"}
        style={{
          fontSize: "14px",
          border: "none",
          outline: "none",
          flex: 1,
          color: "#000",
          backgroundColor: "transparent",
        }}
        readOnly
        onClick={onClick}
      />
      {value && (
        <img
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          src={
            "data:image/svg+xml;utf8," +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
            )
          }
          alt="Clear"
          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 8 }}
        />
      )}
      <img
        src={CalendorIcon}
        alt="Calendar Icon"
        style={{ height: "20px", width: "20px", cursor: "pointer" }}
        onClick={onClick}
      />
    </div>
  )
);
CustomInput.displayName = "CustomInput";

function ReportMaster() {
  // --- Dates: split by tab to prevent collisions ---
  const [summRange, setSummRange] = useState<[Date | null, Date | null]>([null, null]); // Summary header range
  const [trxRange, setTrxRange] = useState<[Date | null, Date | null]>([null, null]);   // Transactions header range

  // active range based on tab
  const [isSwitch, setIsSwitch] = useState(false); // false = Summary, true = Transactions
  const activeRange = isSwitch ? trxRange : summRange;
  const [startDate, endDate] = activeRange;

  // --- Search (Rewards-style debounce) ---
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debouncerRef = useRef<any>(null);
  useEffect(() => {
    debouncerRef.current = debounce((q: string) => setDebouncedSearch(q), 300);
    return () => debouncerRef.current?.cancel?.();
  }, []);
  useEffect(() => {
    debouncerRef.current?.(searchQuery);
  }, [searchQuery]);

  // --- Filter modal(s) with Rewards-style fetch gating ---
  const [shouldFetchFilters, setShouldFetchFilters] = useState(false);
  const [isFilterOpenSummary, setIsFilterOpenSummary] = useState(false);
  const [isFilterOpenTrx, setIsFilterOpenTrx] = useState(false);

  // --- Keep applied filters separate per tab ---
  const [appliedFiltersSummary, setAppliedFiltersSummary] = useState<ReportsFilterPayload | null>(null);
  const [appliedFiltersTransactions, setAppliedFiltersTransactions] = useState<ReportsFilterPayload | null>(null);

  // --- Rewards-style dynamic search placeholder ---
  const SEARCH_PLACEHOLDERS = {
    summary: "Search by PMI Entity Code ,User Name, Outlet Name, Location",
    transactions: "Search by PMI Entity Code, User Name, Status",
  };
  const searchPlaceholder = isSwitch ? SEARCH_PLACEHOLDERS.transactions : SEARCH_PLACEHOLDERS.summary;

  // --- Normalize initial filters for popups (undefined vs null) ---
  const normalizeToInitialFilters = (f: ReportsFilterPayload | null): any | null => {
    if (!f) return null;
    const copy: any = { ...f };
    Object.keys(copy).forEach((k) => {
      if (copy[k] === null) copy[k] = undefined;
    });
    return copy;
  };

  // --- Open correct filter modal ---
  const openFilterForCurrentView = () => {
    setShouldFetchFilters(true);
    if (isSwitch) setIsFilterOpenTrx(true);
    else setIsFilterOpenSummary(true);
  };

  // --- After a popup fetched its initial options ---
  const handleFilterPopupFetched = () => setShouldFetchFilters(false);

  // --- Filter apply handlers ---
  const handleApplySummary = (payload: ReportsFilterPayload) => {
    const pmiArray = payload.pmi_entity_code ?? payload.pmiEntityCodes ?? null;

    setAppliedFiltersSummary({
      locations: payload.locations ?? null,
      outletTypes: payload.outletTypes ?? null,
      outletNames: payload.outletNames ?? null,
      pmiEntityCodes: pmiArray,
      pmi_entity_code: pmiArray,
      lastloginStart: payload.lastloginStart ?? null,
      lastloginEnd: payload.lastloginEnd ?? null,
      status: payload.status ?? null,
      memberSinceStart: payload.memberSinceStart ?? null,
      memberSinceEnd: payload.memberSinceEnd ?? null,
      backendFilters: payload.backendFilters ?? undefined,
      user_salesforce_id:
        (payload as any).user_salesforce_id ??
        (payload as any).userSalesforceId ??
        null,
    });

    setIsFilterOpenSummary(false);
  };

  const handleApplyTrx = (payload: ReportsFilterPayload) => {
    setAppliedFiltersTransactions({ ...payload });
    setIsFilterOpenTrx(false);
  };

  // --- Overlay style ---
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 2000,
    padding: 16,
  };

  // ---------------------------------------------------------------------------
  // 🔹 Sync header ranges ONLY into their own tab keys
  // ---------------------------------------------------------------------------
  const toYmd = (d: Date | null) =>
    d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : null;

  // Summary header → lastlogin*
  useEffect(() => {
    const [s, e] = summRange;
    const startStr = toYmd(s);
    const endStr = toYmd(e);
    setAppliedFiltersSummary((prev) => {
      const base = prev ?? {};
      return { ...base, lastloginStart: startStr, lastloginEnd: endStr };
    });
  }, [summRange]);

  // TRX header → contract_since_*
  useEffect(() => {
    const [s, e] = trxRange;
    const startStr = toYmd(s);
    const endStr = toYmd(e);
    setAppliedFiltersTransactions((prev) => {
      const base = prev ?? {};
      return { ...base, contract_since_from: startStr, contract_since_to: endStr };
    });
  }, [trxRange]);

  return (
    <Box sx={{ background: "#fff", paddingTop: "30px" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 32px" }}>
        <Box>
          <Link href="#" sx={{ textDecoration: "none", fontSize: "14px", fontWeight: "500", color: "#696A6B", paddingRight: "10px" }}>
            Master & outlet report
          </Link>
          <Typography variant="h3" sx={{ fontSize: "24px", fontWeight: "600", color: "#000", marginTop: "16px" }}>
            Master & outlet report
          </Typography>
        </Box>

        {/* 🔁 MOVED HERE: Date Picker (was in lower bar) */}
        <Box sx={{ display: "flex" }}>
          <Box>
            {/* <DatePicker
              selectsRange
              startDate={startDate || undefined}
              endDate={endDate || undefined}
              onChange={(update: [Date | null, Date | null] | null) => {
                if (isSwitch) setTrxRange(update ?? [null, null]);
                else setSummRange(update ?? [null, null]);
              }}
              customInput={
                <CustomInput
                  onClear={() => {
                    if (isSwitch) {
                      setTrxRange([null, null]);
                      setAppliedFiltersTransactions((prev) =>
                        prev ? { ...prev, contract_since_from: null, contract_since_to: null } : prev
                      );
                    } else {
                      setSummRange([null, null]);
                      setAppliedFiltersSummary((prev) =>
                        prev ? { ...prev, lastloginStart: null, lastloginEnd: null } : prev
                      );
                    }
                  }}
                />
              }
              renderCustomHeader={({ date, decreaseMonth, increaseMonth, decreaseYear, increaseYear }) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button onClick={decreaseYear} aria-label="Previous year">
                    <img src={doubleArrowLeft} alt="Previous year" />
                  </button>
                  <button onClick={decreaseMonth} aria-label="Previous month">
                    <img src={arrowLeft} alt="Previous month" />
                  </button>

                  <span style={{ margin: "0 10px" }}>
                    {date.toLocaleString("default", { year: "numeric", month: "long" })}
                  </span>

                  <button onClick={increaseMonth} aria-label="Next month">
                    <img src={arrowRight} alt="Next month" />
                  </button>
                  <button onClick={increaseYear} aria-label="Next year">
                    <img src={doubleArrowRight} alt="Next year" />
                  </button>
                </div>
              )}
            /> */}
          </Box>
        </Box>
      </Box>

      {/* Search + (now) Filter/Toggle on the right */}
      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "16px", padding: "0 32px 30px" }}>
        <Box sx={{ maxWidth: "520px", flexGrow: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            type="search"
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ height: "20px", width: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": { border: "2px solid #F5F5F5" },
                height: "48px",
              },
            }}
          />
        </Box>

        {/* 🔁 MOVED HERE: Filter button + Summary/Transactions toggle (was in header) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={openFilterForCurrentView}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              bgcolor: "#C8172F",
              color: "#fff",
              borderColor: "#C8172F",
              borderRadius: "8px",
              height: "48px",
              padding: "0px 18px",
              "&:hover": { bgcolor: "#a31425", borderColor: "#a31425" },
            }}
          >
            Filter
          </Button>

          <ButtonGroup variant="outlined" sx={{ mr: 0 }}>
            <Button
              onClick={() => setIsSwitch(false)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: !isSwitch ? "#C8172F" : "transparent",
                color: !isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "48px",
                "&:hover": { bgcolor: !isSwitch ? "#a31425" : "rgba(200,23,47,0.1)", borderColor: "#a31425" },
              }}
            >
              Summary
            </Button>

            <Button
              onClick={() => setIsSwitch(true)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: isSwitch ? "#C8172F" : "transparent",
                color: isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "48px",
                "&:hover": { bgcolor: isSwitch ? "#a31425" : "rgba(200,23,47,0.1)", borderColor: "#a31425" },
              }}
            >
              Transactions
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Tables */}
      <Box sx={{ width: "100%", background: "#f9fbfc", borderTop: "1px solid #d3d3d3" }}>
        {isSwitch ? (
          <ReportsMasterTrTable
            startDate={trxRange[0]}
            endDate={trxRange[1]}
            searchQuery={debouncedSearch}
            appliedFilters={appliedFiltersTransactions ?? null}
          />
        ) : (
          <ReportsMasterTable
            startDate={summRange[0]}
            endDate={summRange[1]}
            searchQuery={debouncedSearch}
            appliedFilters={appliedFiltersSummary ?? null}
          />
        )}
      </Box>

      {/* Filter modals (separate) */}
      {isFilterOpenSummary && (
        <div
          style={overlayStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpenSummary(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", width: "auto" }}>
            <ReportsFilterPopup
              isOpen={isFilterOpenSummary}
              shouldFetch={shouldFetchFilters}
              onFetched={handleFilterPopupFetched}
              onApplyFilters={handleApplySummary}
              onCancel={() => setIsFilterOpenSummary(false)}
              dateRange={summRange}                 // Summary header range
              setDateRange={setSummRange}           // keep in sync
              initialFilters={normalizeToInitialFilters(appliedFiltersSummary)}
            />
          </div>
        </div>
      )}

      {isFilterOpenTrx && (
        <div
          style={overlayStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpenTrx(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", width: "auto" }}>
            <ReportsTrFilterPopup
              isOpen={isFilterOpenTrx}
              shouldFetch={shouldFetchFilters}
              onFetched={handleFilterPopupFetched}
              onApplyFilters={handleApplyTrx}
              onCancel={() => setIsFilterOpenTrx(false)}
              dateRange={trxRange}                  // TRX header range
              setDateRange={setTrxRange}            // keep in sync
              initialFilters={normalizeToInitialFilters(appliedFiltersTransactions)}
            />
          </div>
        </div>
      )}

      {/* Bottom Back button */}
      <Box
        sx={{
          mt: 6,
          px: 4,
          py: 2,
          borderTop: "1px solid #eee",
          backgroundColor: "#fff",position:"fixed",
          bottom:"0",
          width:"100%"
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          onClick={() => window.history.back()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#C8172F",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
}

export default ReportMaster;
